import {Box, Button, styled, Stack, useTheme, Container} from "@mui/material";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import {ChangeEvent, useContext, useEffect, useState} from "react";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import {Delete} from "@mui/icons-material";
import {imageValidTypes, maxSizeImageFile, palette} from "@/constants";
import {INotificationSeverity, NotificationContext} from "@buildwitt/shapeshift-identity";

// Implement Uploading, drag and drop when working on saving settings
interface EmptyLogoContentProps {
	label?: string;
	emptyLabelSuggestion?: string;
}
const EmptyLogoContent = ({
	label,
	emptyLabelSuggestion
}: EmptyLogoContentProps) => {
	const {t} = useTranslation(["common", "settings"]);
	const theme = useTheme();
	return (
		<Stack spacing={1} data-testid="emptyLogoContentStack">
			<Container
				sx={{width: "100%", textAlign: "center"}}
				data-testid="emptyLogoContentContainer"
			>
				<UploadFileOutlinedIcon
					sx={{color: theme.palette.primary.main}}
					data-testid="emptyLogoContentIcon"
				/>
			</Container>
			<Typography
				typography="subtitle1"
				sx={{
					color: theme.palette.text.primary,
					textAlign: "center"
				}}
				data-testid="emptyLogoContentPrimaryText"
			>
				{label}
			</Typography>
			<Typography
				typography="caption"
				sx={{
					color: theme.palette.secondary.dark,
					textAlign: "center"
				}}
				data-testid="emptyLogoContentSecondaryText"
			>
				{t("formatsSupported", {ns: "settings"})}
			</Typography>
			<Typography
				typography="caption"
				sx={{
					color: theme.palette.secondary.dark,
					textAlign: "center"
				}}
				data-testid="emptyLogoContentRecommendationText"
			>
				{emptyLabelSuggestion}
			</Typography>
		</Stack>
	);
};

export interface LogoSelectorProps {
	onImageSelected?: (file?: File) => void;
	label?: string;
	emptyLabel?: string;
	companyLogo?: string;
	showDeleteIcon?: boolean;
	emptyLabelSuggestion?: string;
}

const LogoSelector = ({
	onImageSelected,
	companyLogo,
	label,
	emptyLabel,
	showDeleteIcon,
	emptyLabelSuggestion
}: LogoSelectorProps) => {
	const [image, setImage] = useState<string | undefined>(companyLogo);
	const {t} = useTranslation(["common", "settings"]);
	const {notify} = useContext(NotificationContext);

	useEffect(() => {
		setImage(companyLogo);
	}, [companyLogo]);

	const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];

		if (file) {
			const fileType = file.type;
			const fileSize = file.size;

			if (!imageValidTypes.includes(fileType)) {
				notify(t("invalid_file_type", "Invalid file format. Try again."), "", INotificationSeverity.error, 5000);
				return;
			}

			if (fileSize > maxSizeImageFile) {
				notify(t("file_too_large", "Please choose a file smaller than {{number}} MB.", {number: "3"}), "", INotificationSeverity.error, 5000);
				return;
			}

			setImage(URL.createObjectURL(file));
			if (onImageSelected) {
				onImageSelected(file);
			}
		} else setImage(undefined);
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === "Enter") {
			document.getElementById("uploadProfilePicture")?.click();
			event.preventDefault();
		}
	};

	return (
		<Box sx={{position: "relative"}} data-testid="logoSelectorBox">
			<CompanyLogoDiv
				component="fieldset"
				data-testid="logoSelectorCompanyLogoDiv"
				onClick={() => document.getElementById("uploadProfilePicture")!.click()}
				sx={{cursor: "pointer"}}
			>
				<legend style={{
					textAlign: "left"
				}}>
					<LabelOutline data-testid="logoSelectorTitleLabel" sx={{color: palette.RxdDescription}}>
						{label}
					</LabelOutline>
				</legend>
				{image ? (
					<img
						src={image}
						style={{width: "100%", height: "100%", objectFit: "contain"}}
						alt={label}
						data-testid="logoSelectorImage"
					/>
				) : (
					<EmptyLogoContent
						label={emptyLabel}
						emptyLabelSuggestion={emptyLabelSuggestion}
					/>
				)}
			</CompanyLogoDiv>
			{showDeleteIcon && image && (
				<Button
					component="span"
					id="deleteButton"
					variant="outlined"
					sx={{
						minWidth: "45px",
						position: "absolute",
						marginLeft: 25,
						bottom: -10,
						backgroundColor: "#302120",
						borderColor: "error.main",
						"&:hover": {
							backgroundColor: "#4C2724",
							borderColor: "error.main"
						}
					}}
					color="error"
					data-testid="logoSelectorDeleteButton"
					onClick={() => {
						setImage(undefined);
						if (onImageSelected) {
							onImageSelected(undefined);
						}
					}}
				>
					<Delete fontSize="small" />
				</Button>
			)}
			<input
				id="uploadProfilePicture"
				accept={imageValidTypes.join(",")}
				type="file"
				hidden
				onChange={handleImageChange}
				data-testid="logoSelectorFileInput"
			/>
			<label
				htmlFor="uploadProfilePicture"
				data-testid="logoSelectorFileUploadLabel"
			>
				<Button
					component="span"
					id="uploadButton"
					variant="contained"
					sx={{
						minWidth: "52px",
						position: "absolute",
						marginLeft: 33,
						bottom: -10
					}}
					data-testid="logoSelectorFileUploadButton"
					onKeyDown={handleKeyDown}
				>
					<AddPhotoAlternateOutlinedIcon fontSize="small" />
				</Button>
			</label>
		</Box>
	);
};

const CompanyLogoDiv = styled(Box)(({theme}) => ({
	borderColor: "#ffffff3b",
	width: "270px",
	height: "190px",
	borderWidth: 1,
	borderStyle: "solid",
	borderRadius: "4px",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	"&:hover": {
		borderColor: theme.palette.secondary.light
	}
}));

const LabelOutline = styled(Typography)(({theme}) => ({
	color: theme.palette.text.secondary,
	fontSize: "14px"
}));

export default LogoSelector;
